<template>
  <div class="email-message-page py-2 px-1 pa-sm-3">
    <!-- 13 Jul 2026 JCH: no flex-wrap — the X stays on the top row; the note button label wraps instead. -->
    <div
      v-if="showLastConnectSticky"
      class="email-last-connect-strip d-flex align-start grey lighten-4 rounded px-2 px-sm-3 py-2 mb-2"
    >
      <v-icon small class="mr-2 mt-1 flex-shrink-0" color="success">mdi-check-circle</v-icon>
      <div class="email-last-connect-copy flex-grow-1 mr-1">
        <p v-if="lastConnectStickyExplain" class="body-2 mb-1 grey--text text--darken-3">{{ lastConnectStickyExplain }}</p>
        <v-btn
          text
          small
          color="secondary darken-1"
          class="email-last-connect-note-btn px-1 ml-n1 mb-0"
          @click="viewLastConnectedNote"
        >
          <v-icon left small>mdi-note-text-outline</v-icon>
          {{ lastConnectStickyLabel }}
        </v-btn>
      </div>
      <v-btn icon small class="mb-0 flex-shrink-0" title="Dismiss" @click="clearLastConnectSticky">
        <v-icon small>mdi-close</v-icon>
      </v-btn>
    </div>

    <v-card v-if="pageEnd" class="elevation-0 rounded-0 borderless email-page-end-card">
      <v-card-text class="pa-3 pa-sm-4">
        <div class="d-flex align-start">
          <v-icon color="secondary darken-1" class="mr-2 flex-shrink-0">mdi-email-check-outline</v-icon>
          <div class="flex-grow-1">
            <p class="body-1 font-weight-medium mb-1">{{ pageEndTitle }}</p>
            <p v-if="pageEndActionMsg" class="body-2 mb-2">{{ pageEndActionMsg }}</p>
            <p v-if="pageEndHint" class="body-2 grey--text text--darken-1 mb-2">{{ pageEndHint }}</p>
            <p v-if="lastConnectedNid" class="body-2 grey--text text--darken-2 mb-2">
              {{ lastConnectPageEndExplain }}
            </p>
            <div class="d-flex flex-wrap align-center email-page-end-actions">
              <v-btn
                v-if="lastConnectedNid"
                text
                color="secondary darken-1"
                class="email-last-connect-note-btn mb-2 mr-2 px-1 ml-n1"
                small
                @click="viewLastConnectedNote"
              >
                <v-icon left small>mdi-note-text-outline</v-icon>
                {{ lastConnectStickyLabel }}
              </v-btn>
              <v-btn
                v-if="pageEndHasMoreListPages && !embedded"
                color="secondary"
                class="mb-2 mr-2"
                @click="backToMailList"
              >
                Back to message list
              </v-btn>
              <v-btn
                :color="pageEndHasMoreListPages && !embedded ? 'grey' : 'secondary'"
                :text="pageEndHasMoreListPages && !embedded"
                class="mb-2"
                @click="closePopupWindow"
              >
                {{ embedded ? 'Close' : 'Close window' }}
              </v-btn>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <v-card v-else class="elevation-0 rounded-0 borderless">
      <v-card-text class="email-message-card-text pt-0 pb-2 px-2 px-sm-4">
        <div v-if="loading" class="body-2 grey--text">Loading…</div>
        <div v-else-if="missingParams" class="body-2 error--text">
          Open this window from the Email page (mailbox and email selection are required).
        </div>
        <div v-else-if="sessionTrashedOnly" class="email-trashed-only grey lighten-4 pa-3 pa-sm-4 rounded">
          <div class="d-flex align-start">
            <v-icon color="error darken-1" class="mr-2 flex-shrink-0">mdi-delete-outline</v-icon>
            <div>
              <p class="body-2 mb-1 font-weight-medium">Moved to Trash</p>
              <p class="caption mb-0 grey--text text--darken-1">
                This email was removed from the message list. It cannot be connected or restored.
              </p>
            </div>
          </div>
        </div>
        <div v-else-if="!detail" class="body-2 grey--text">Email not found.</div>
        <div v-else>
          <div class="email-connect-panel px-2 px-sm-3 pt-2 pb-2 rounded mb-2">
            <div class="email-connect-panel-title subtitle-2 mb-2 d-flex align-center">
              <span>{{ connectedNoteNid || detail.connected ? 'Connected to firm' : 'Connect to firm (as a note)' }}</span>
              <v-spacer/>
              <!-- 13 Jul 2026 JCH: ‹ › browse the opener's message list from the popup (pane/full get these in the pane bar). -->
              <template v-if="!embedded && browsePos">
                <span
                  class="email-browse-pos caption grey--text text--darken-1 mr-1 font-weight-regular"
                  title="Position on this page of the message list"
                >{{ browsePos.index + 1 }} of {{ browsePos.count }}</span>
                <v-btn
                  icon
                  small
                  class="email-browse-newer"
                  title="Newer email"
                  :disabled="!browsePos.hasNewer"
                  @click="browseNeighbor('newer')"
                >
                  <v-icon small>mdi-chevron-left</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  class="email-browse-older"
                  title="Older email"
                  :disabled="!browsePos.hasOlder"
                  @click="browseNeighbor('older')"
                >
                  <v-icon small>mdi-chevron-right</v-icon>
                </v-btn>
              </template>
            </div>

            <!-- Form: this-note inputs -->
            <div class="email-connect-band email-connect-band--form">
              <v-autocomplete
                ref="connectFirmField"
                v-model="selectedFirm"
                clearable
                dense
                :error="connectFirmError"
                :error-messages="connectFirmError ? connectFirmRequiredMsg : ''"
                hide-details="auto"
                hide-no-data
                hide-selected
                item-text="text"
                return-object
                class="email-firm-autocomplete mb-0"
                :items="firmItems"
                :loading="firmSearchLoading"
                :search-input.sync="firmSearchInput"
                @blur="onFirmFieldBlur"
                :label="connectedNoteNid || detail.connected ? 'Firm' : 'Search firms or enter FID'"
                placeholder="Type FID, sort name, insured, phone…"
                no-filter
                :disabled="connectFieldsDisabled"
              />
              <v-text-field
                v-model="connectReLine"
                class="email-connect-re mt-6 mb-0"
                dense
                hide-details="auto"
                label="Re line"
                :disabled="connectFieldsDisabled"
                maxlength="500"
                counter
              />
              <div
                v-if="!connectedNoteNid"
                class="email-connect-fields mt-1"
              >
                <v-select
                  v-model="connectStaff"
                  dense
                  hide-details="auto"
                  item-text="username"
                  item-value="username"
                  class="email-connect-staff"
                  :items="staffUserItems"
                  label="Staff / For"
                  :disabled="connectFieldsDisabled || staffUsersLoading"
                  :loading="staffUsersLoading"
                />
                <v-select
                  v-model="connectStatus"
                  dense
                  hide-details="auto"
                  item-text="text"
                  item-value="text"
                  class="email-connect-status mb-0"
                  :items="noteStatusItems"
                  label="Note status"
                  :disabled="connectFieldsDisabled"
                />
              </div>
            </div>

            <div
              v-if="connectedNoteNid"
              class="email-connect-outcome white pa-2 pa-sm-3 rounded mt-2"
              :class="connectedPartial ? 'email-connect-outcome--partial' : 'email-connect-outcome--success'"
            >
              <div class="d-flex flex-wrap align-center email-connect-success-row">
                <v-icon
                  small
                  class="mr-1 flex-shrink-0"
                  :color="connectedPartial ? 'warning darken-2' : 'success'"
                >
                  {{ connectedPartial ? 'mdi-alert-circle-outline' : 'mdi-check-circle' }}
                </v-icon>
                <span class="body-2 flex-grow-1">
                  {{ connectedResultMsg }}<span v-if="!connectedPartial && connectedTrashed"> Email moved to trash.</span>
                </span>
                <!-- 13 Jul 2026 JCH: shrinkable — flex-shrink-0 pushed the trash button out of a narrow pane. -->
                <div class="email-connect-success-actions d-flex flex-wrap align-center">
                  <v-btn
                    color="secondary darken-1"
                    class="email-connect-btn mb-0"
                    :small="$vuetify.breakpoint.smAndDown"
                    @click="viewConnectedFirm"
                  >
                    <v-icon left small>mdi-note-text-outline</v-icon>
                    {{ viewConnectedNoteLabel }}
                  </v-btn>
                  <v-btn
                    v-if="showPostConnectTrash && !connectedTrashed"
                    small
                    outlined
                    color="error"
                    class="email-connect-btn ml-2 mb-0"
                    :disabled="busy"
                    @click="confirmTrashSingle = true"
                  >
                    {{ postConnectTrashLabel }}
                  </v-btn>
                </div>
              </div>
              <v-checkbox
                v-if="showPostConnectTrashPref"
                v-model="trashAfterConnect"
                class="email-trash-after-connect email-trash-after-connect--outcome mt-1 mb-0 pt-0"
                dense
                hide-details="auto"
                hint="Saved on this browser only."
                persistent-hint
                :label="trashAfterConnectLabel"
                @change="onTrashAfterConnectChange"
              />
            </div>
            <template v-else>
              <!-- Commit: Connect ▾ (trash/open-note) + trash + go-to (shared) -->
              <div
                v-if="showConnectActions"
                class="email-connect-band email-connect-band--commit"
              >
                <div class="email-connect-split d-flex align-center flex-shrink-0">
                  <v-tooltip bottom max-width="320" :disabled="connectMenuOpen">
                    <template v-slot:activator="{ on, attrs }">
                      <span class="d-inline-flex" v-on="on" v-bind="attrs">
                        <v-btn
                          color="secondary"
                          class="email-connect-btn email-connect-btn--main email-connect-btn--split-main mb-0"
                          :x-large="$vuetify.breakpoint.mdAndUp"
                          :loading="connectBusy"
                          :disabled="!canSubmitConnect"
                          @click="submitConnect"
                        >
                          Connect
                        </v-btn>
                      </span>
                    </template>
                    <span>{{ connectWithAdvanceTooltip }}</span>
                  </v-tooltip>
                  <v-menu
                    v-model="connectMenuOpen"
                    offset-y
                    :close-on-content-click="false"
                    min-width="300"
                    max-width="360"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        color="secondary"
                        class="email-connect-btn email-connect-btn--split-caret mb-0 px-1"
                        :x-large="$vuetify.breakpoint.mdAndUp"
                        :disabled="connectBusy"
                        v-bind="attrs"
                        v-on="on"
                        aria-label="Connect options"
                      >
                        <v-icon small>mdi-menu-down</v-icon>
                      </v-btn>
                    </template>
                    <v-list dense class="email-connect-menu py-1">
                      <v-subheader class="email-connect-menu-subhead px-3">
                        After connect · this browser only
                      </v-subheader>
                      <div class="email-connect-menu-prefs px-3 pb-2">
                        <v-checkbox
                          v-model="trashAfterConnect"
                          class="email-trash-after-connect mt-0 pt-0 mb-1"
                          dense
                          hide-details
                          :disabled="connectBusy"
                          :label="trashAfterConnectLabel"
                          @change="onTrashAfterConnectChange"
                        />
                        <v-checkbox
                          v-model="openNoteAfterConnect"
                          class="email-open-note-after-connect mt-0 pt-0 mb-0"
                          dense
                          hide-details
                          :disabled="connectBusy"
                          label="Open note in firm after connect"
                          @change="onOpenNoteAfterConnectChange"
                        />
                      </div>
                    </v-list>
                  </v-menu>
                </div>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-on="on" v-bind="attrs">
                      <v-btn
                        icon
                        class="email-connect-trash-btn mb-0 flex-shrink-0"
                        :disabled="trashDisabled"
                        @click="confirmTrashSingle = true"
                      >
                        <v-icon>mdi-delete-outline</v-icon>
                      </v-btn>
                    </span>
                  </template>
                  <span>{{ trashWithoutConnectTooltip }}</span>
                </v-tooltip>
                <!-- 13 Jul 2026 JCH: caption above the selects (not a floated field label) so the
                     pair stays content-sized and fits the narrow side pane on one line. -->
                <div class="email-auto-advance-pair flex-grow-1">
                  <div class="email-auto-advance-caption grey--text text--darken-1">
                    After trash or successful connect · this browser only
                  </div>
                  <div class="email-auto-advance-selects d-flex align-center flex-wrap">
                    <v-select
                      v-model="autoAdvanceMode"
                      aria-label="After trash or successful connect"
                      class="email-auto-advance-mode mt-0 pt-0 mb-0"
                      dense
                      hide-details
                      :items="autoAdvanceModeOptions"
                      item-text="text"
                      item-value="value"
                      :disabled="connectBusy"
                      @change="onAutoAdvanceModeChange"
                    />
                    <v-select
                      v-if="autoAdvanceMode === 'go'"
                      v-model="autoAdvanceGoTo"
                      aria-label="Go to"
                      class="email-auto-advance-goto mt-0 pt-0 mb-0"
                      dense
                      hide-details
                      :items="autoAdvanceGoToOptions"
                      item-text="text"
                      item-value="value"
                      :disabled="connectBusy"
                      @change="onAutoAdvanceGoToChange"
                    />
                  </div>
                </div>
              </div>
              <p v-if="detail.connected && !needsEmailApi('connectedBucket')" class="caption mb-0 mt-2 grey--text text--darken-2">
                This email is already connected (see Connected tab on the Email page).
              </p>
              <p v-else-if="detail.connected" class="caption mb-0 mt-2 grey--text text--darken-2">
                This email is already connected to a firm note.
              </p>
              <p v-else-if="detail.deleted && !needsEmailApi('trashBucket')" class="caption error--text mb-0 mt-2">
                This email is in Trash — restore it from the Email page to connect.
              </p>
              <p v-else-if="detail.deleted" class="caption error--text mb-0 mt-2">
                This email is in Trash and cannot be connected from here.
              </p>
            </template>
          </div>
          <div class="email-meta pa-2 pa-sm-3 rounded body-2 mb-1">
            <div class="email-meta-line"><strong>From:</strong> {{ detail.from }}</div>
            <div class="email-meta-line"><strong>Subject:</strong> {{ detail.subject }}</div>
            <div class="email-meta-line"><strong>Date:</strong> {{ formatReceived(detail.received_at) }}</div>
            <div class="email-meta-line"><strong>To:</strong> {{ detail.to_address || detail.to }}</div>
            <!-- 24 Aug 2026 JCH: NC-1641 — popup matches the note stamp, which now carries Cc -->
            <div class="email-meta-line" v-if="detail.cc"><strong>Cc:</strong> {{ detail.cc }}</div>
          </div>
          <div class="email-lower px-1 px-sm-2 pt-1">
          <div v-if="attachmentStaging.length" class="my-1">
            <div class="d-flex align-center flex-wrap mb-n1 email-attachments-heading">
              <span class="caption grey--text text--darken-1">Attachments</span>
              <information-dialog-icon
                class="ml-1 align-self-center"
                :dialog-width="520"
                icon-color="grey darken-1"
                icon-style="width: 28px; height: 28px"
                title="Attachments"
              >
                <p class="body-2">
                  Each attachment defaults to its <strong>filename</strong> as the file description. Edit the description before Connect if you want a shorter label on the firm note.
                </p>
                <p class="body-2 mb-0">
                  On Connect, files keep their original filename; the description you set here is stored as the firm file label, same as a manual upload.
                  The original email body is attached as <strong>email.txt</strong> and/or
                  <strong>email.html</strong> (also in the firm's files).
                </p>
              </information-dialog-icon>
            </div>
            <div
              v-for="(att, idx) in attachmentStaging"
              :key="`${idx}-${att.filename}`"
              class="d-flex align-start flex-wrap email-attachment-row mb-1"
            >
              <button-tip
                btnstyle="margin: 4px 10px 0 0; flex-shrink: 0; transform: translateY(6px)"
                icon
                iname="mdi-download"
                istyle="color: blue"
                tip="Download"
                xsmall
                @click="downloadAttachment(att)"
              />
              <v-text-field
                v-model="att.file_description"
                dense
                hide-details
                single-line
                class="email-attachment-desc flex-grow-1"
                style="min-width: 180px"
                :disabled="connectFieldsDisabled"
              />
            </div>
          </div>
          <div v-else-if="showInlineOnlyAttachmentNote" class="my-1">
            <span class="caption grey--text text--darken-1 d-block mb-1">Attachments</span>
            <div class="email-attachments-inline-note grey lighten-4 pa-2 pa-sm-3 rounded">
              <p class="mb-1">
                <strong>No downloadable attachments:</strong>
                The paperclip in the message list sometimes means pictures built into the email, not a PDF or other file.
              </p>
              <p class="mb-0">
                Files you can connect appear here with a Download button.
              </p>
            </div>
          </div>
          <div
            class="email-message-body-section"
            :class="attachmentStaging.length || showInlineOnlyAttachmentNote ? 'mt-2' : 'mt-1'"
          >
            <span class="caption grey--text text--darken-1 d-block mb-1">Message</span>
            <iframe
              ref="emailBodyIframe"
              class="email-body-iframe"
              sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
              :srcdoc="messageIframeSrcdoc"
              title="Email message body"
              width="100%"
              @load="onEmailBodyIframeLoad"
            />
          </div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <ActionDialog
      max-width="520"
      :value="confirmTrashSingle"
      title="Move to trash?"
      confirm-text="Move to trash"
      confirm-color="error"
      divider
      close-text="Cancel"
      persistent
      :disabled="busy"
      :confirm-loading="busy"
      @close="confirmTrashSingle = false"
      @input="confirmTrashSingle = $event"
      @confirm="runTrashCurrentMessage"
    >
      <p class="mt-4">
        Move this email to <strong>Trash</strong>?
        <span v-if="needsEmailApi('trashBucket')">
          It will no longer show in the message list and cannot be brought back.
        </span>
        <span v-else>
          You can find it later under the Trash tab on the Email page.
        </span>
      </p>
      <p v-if="trashOtherMailboxWarning" class="warning--text text--darken-3 font-weight-medium mb-0">
        <v-icon class="mr-1 mt-n1" color="warning darken-3" small>mdi-alert</v-icon>{{ trashOtherMailboxWarning }}
      </p>
    </ActionDialog>

    <snack
      v-if="$_snack.cmpnt === 'EmailMessage'"
      v-model="$_snack.open"
      :color="$_snack.color"
      :msg="$_snack.msg"
    />
    <ErrorDialog ref="errorDialog"/>
  </div>
</template>

<script>
import ActionDialog from '@/components/ActionDialog';
import ErrorDialog from '@/components/ErrorDialog';
import InformationDialogIcon from '@/components/InformationDialogIcon';
import { createNuEmailClient } from '@/servicehandlers/nuEmailApi';
import {
  appendTrashAfterConnectSnack,
  connectHardFailDialogOptions,
  CONNECT_PARTIAL_BANNER_HINT,
  connectRowSnackMessage,
  connectSnackForAdvance,
  formatReceived,
  isFullConnectSuccess,
  readTrashAfterConnectPref,
  runEmailConnectBatch,
  trashMessagesAfterConnect,
  writeTrashAfterConnectPref,
} from '@/components/search/email/emailConnectCore';
import {
  AUTO_ADVANCE_END_NOTICE,
  AUTO_ADVANCE_GO_TO_OPTIONS,
  AUTO_ADVANCE_MODE_GO,
  AUTO_ADVANCE_MODE_OPTIONS,
  autoAdvanceGoToFromPref,
  autoAdvanceModeFromPref,
  connectWithAdvanceTooltip as connectWithAdvanceTooltipText,
  lastConnectPageEndExplain as lastConnectPageEndExplainText,
  lastConnectStickyActionLabel,
  lastConnectStickyExplain as lastConnectStickyExplainText,
  readAutoAdvancePref,
  readOpenNoteAfterConnectPref,
  resolveAutoAdvancePref,
  trashWithoutConnectTooltip as trashWithoutConnectTooltipText,
  writeAutoAdvancePref,
  writeOpenNoteAfterConnectPref,
} from '@/components/search/email/emailAutoAdvance';
import {
  applyFailedEmailImgFallback,
  bindIframeAutoResize,
  buildNoteIframeSrcdoc,
  gmailAttachmentUrl,
  resolveInlineImageAttachment,
} from '@/components/search/email/emailBodyHtml';
import {
  CONNECT_FIRM_REQUIRED_MSG,
  EMAIL_IFRAME_MAX_HEIGHT,
  needsEmailApi,
  otherMailboxTrashWarning,
  TRASH_AFTER_CONNECT_LABEL,
} from '@/components/search/email/emailConstants';
import { FirmsAPIService } from '@/servicehandlers/FirmsAPIService';
import { UsersAPIService } from '@/servicehandlers/UsersAPIService';

const firmsAPIService = new FirmsAPIService();
const usersAPIService = new UsersAPIService();
const FALLBACK_NOTE_STATUSES = ['archive', 'check', 'hold', 'low', 'normal', 'urgent', 'xtreme'];
const FIRM_SEARCH_DEBOUNCE_MS = 280;
const FID_SEARCH_RE = /^\d+$/;

function firmSearchPayloadForFid(fid) {
  return {
    page: 1,
    perPage: 1,
    sortBy: null,
    sortDesc: false,
    checkedColumns: ['fid', 'sort', 'insured', 'name1', 'name2'],
    whereColumns: { fid },
    includeCheckColumns: {},
    includeColumns: [],
    simple: false,
    getall: false,
  };
}

function firmRowsFromFidResponse(data) {
  if (data && Array.isArray(data.result)) return data.result;
  return Array.isArray(data) ? data : [];
}

function firmSearchItemsFromRows(rows) {
  return (Array.isArray(rows) ? rows : []).map((r) => {
    const sort = r.sort || '';
    const insured = r.insured || '';
    const fid = r.fid;
    const parts = [sort, insured].filter(Boolean);
    const head = parts.length ? parts.join(' — ') : '';
    const text = head ? `FID ${fid} — ${head}` : `FID ${fid}`; // 1 Jul 2026 JCH: FID first — value used on import
    return { text, fid };
  });
}

export default {
  name: 'EmailMessage',
  components: { ActionDialog, ErrorDialog, InformationDialogIcon },
  // 13 Jul 2026 JCH: embedded = reading pane inside Email.vue; default (popup window) unchanged.
  props: {
    embedded: Boolean,
    /** Same shape as the popup route query: { mailbox_id, message_id, listed_attach }. */
    paneQuery: { type: Object, default: null },
    /** List callbacks when embedded (popup uses window.opener.__NU_EMAIL_LIST__). */
    listApi: { type: Object, default: null },
    /** Share the list's API client when embedded (popup grabs it off window.opener). */
    sharedClient: { type: Object, default: null },
  },
  data() {
    return {
      client: null,
      detail: null,
      loading: false,
      busy: false,
      connectBusy: false,
      connectStaff: '',
      connectStatus: 'normal',
      staffUserItems: [],
      staffUsersLoading: false,
      selectedFirm: null,
      firmItems: [],
      firmSearchInput: '',
      firmSearchLoading: false,
      firmSearchTimer: null,
      connectFirmError: false,
      connectFirmRequiredMsg: CONNECT_FIRM_REQUIRED_MSG,
      trashAfterConnectLabel: TRASH_AFTER_CONNECT_LABEL,
      confirmTrashSingle: false,
      connectReLine: '',
      attachmentStaging: [],
      connectedNoteNid: null, // 6 May 2026 JCH: enables "View firm" after connect
      connectedNoteFid: null,
      connectedPartial: false,
      connectedResultMsg: '',
      connectedTrashed: false,
      sessionTrashed: false, // 1 Jul 2026 JCH: Gmail getMessage omits deleted — track trash in popup session
      trashAfterConnect: readTrashAfterConnectPref(),
      openNoteAfterConnect: readOpenNoteAfterConnectPref(),
      autoAdvance: readAutoAdvancePref(),
      autoAdvanceMode: autoAdvanceModeFromPref(),
      autoAdvanceGoTo: autoAdvanceGoToFromPref(),
      autoAdvanceModeOptions: AUTO_ADVANCE_MODE_OPTIONS,
      autoAdvanceGoToOptions: AUTO_ADVANCE_GO_TO_OPTIONS,
      connectMenuOpen: false,
      lastConnectedNid: null,
      lastConnectedFid: null,
      lastAdvanceOpenedAs: null,
      lastAdvanceUsedFallback: false,
      pageEnd: false,
      pageEndActionMsg: '',
      pageEndHasMoreListPages: false,
      browsePos: null, // popup ‹ › position; null hides the arrows (no opener / not on page)
      _messageStateKey: null,
      _advanceTargetId: null, // set just before auto-advance navigation; consumed by loadMessage
    };
  },
  computed: {
    showLastConnectSticky() {
      // On page-end, Open note in firm lives in the end panel (avoid duplicate chrome).
      return !!(this.lastConnectedNid && !this.connectedNoteNid && !this.pageEnd);
    },
    lastConnectStickyExplain() {
      return lastConnectStickyExplainText({
        openedAs: this.lastAdvanceOpenedAs,
        usedFallback: this.lastAdvanceUsedFallback,
      });
    },
    lastConnectStickyLabel() {
      return lastConnectStickyActionLabel(this.lastConnectedFid);
    },
    lastConnectPageEndExplain() {
      return lastConnectPageEndExplainText(this.lastConnectedFid);
    },
    trashWithoutConnectTooltip() {
      return trashWithoutConnectTooltipText(this.autoAdvance);
    },
    connectOutcomeOpts() {
      return {
        trashAfter: this.trashAfterConnect,
        openNote: this.openNoteAfterConnect,
        autoAdvance: this.autoAdvance,
      };
    },
    connectWithAdvanceTooltip() {
      return connectWithAdvanceTooltipText(this.connectOutcomeOpts);
    },
    pageEndTitle() {
      return this.pageEndHasMoreListPages ? 'Done with this batch' : 'Caught up';
    },
    pageEndHint() {
      if (this.pageEndHasMoreListPages) {
        return 'More mail is waiting on the next page — use the message list to continue.';
      }
      return '';
    },
    messageQuery() {
      return (this.embedded ? this.paneQuery : this.$route.query) || {};
    },
    mailboxId() {
      const q = this.messageQuery.mailbox_id;
      return q === undefined || q === null ? '' : String(q);
    },
    messageId() {
      const q = this.messageQuery.message_id;
      return q === undefined || q === null ? '' : String(q);
    },
    listedWithAttachment() {
      const q = this.messageQuery.listed_attach;
      return q === '1' || q === 'true' || q === true;
    },
    showInlineOnlyAttachmentNote() { // 1 Jul 2026 JCH: list had paperclip; detail may be inline cid: only
      return (
        this.listedWithAttachment
        && !this.loading
        && !!this.detail
        && !this.attachmentStaging.length
      );
    },
    missingParams() {
      return !this.mailboxId || !this.messageId;
    },
    noteStatusItems() {
      const raw = this.$_lists && this.$_lists.nstatus;
      if (Array.isArray(raw) && raw.length) return raw;
      return FALLBACK_NOTE_STATUSES.map((text) => ({ text }));
    },
    messageTrashed() {
      return this.sessionTrashed || !!(this.detail && this.detail.deleted);
    },
    trashOtherMailboxWarning() { // 10 Jul 2026 JCH: popup may hold a colleague's mail — Name (email) from mailbox id when list unavailable
      return otherMailboxTrashWarning(
        [{ mailbox_id: this.mailboxId }], [], this.$store.getters.username, null,
      );
    },
    sessionTrashedOnly() {
      return this.sessionTrashed && !this.connectedNoteNid;
    },
    showConnectActions() {
      return !!(this.detail && !this.detail.connected && !this.detail.deleted && !this.messageTrashed);
    },
    connectFieldsDisabled() {
      return (
        !this.detail
        || this.detail.connected
        || this.detail.deleted
        || this.messageTrashed
        || this.connectedNoteNid
        || this.loading
        || this.connectBusy
      );
    },
    canSubmitConnect() {
      return (
        this.detail
        && !this.detail.connected
        && !this.detail.deleted
        && !this.messageTrashed
        && !this.loading
        && !this.connectBusy
        && !!this.connectStaff
      );
    },
    trashDisabled() {
      return !this.detail || this.messageTrashed || this.loading || this.busy;
    },
    showPostConnectTrash() {
      return !!(this.connectedNoteNid && this.detail && !this.messageTrashed);
    },
    showPostConnectTrashPref() {
      return !!(this.connectedNoteNid && !this.connectedPartial && !this.connectedTrashed);
    },
    viewConnectedNoteLabel() {
      const fid = this.connectedNoteFid;
      return fid != null && fid !== '' ? `Open note in firm · FID ${fid}` : 'Open note in firm';
    },
    postConnectTrashLabel() {
      return this.$vuetify.breakpoint.mdAndUp
        ? 'Move this email to trash'
        : 'Move to trash';
    },
    displayBodyHtml() {
      return (this.detail && this.detail.body_html) || '';
    },
    messageIframeSrcdoc() {
      const html = this.displayBodyHtml;
      return html ? buildNoteIframeSrcdoc(html) : '';
    },
  },
  watch: {
    messageQuery: {
      handler() {
        this.loadMessage({});
      },
      deep: true,
    },
    selectedFirm(val) {
      if (val) this.connectFirmError = false;
    },
    firmSearchInput(val) {
      if (this.connectFirmError) this.connectFirmError = false;
      this.scheduleFirmSearch(val);
    },
    displayBodyHtml() {
      this.$nextTick(() => this.onEmailBodyIframeLoad());
    },
  },
  mounted() {
    if (!this.embedded && typeof document !== 'undefined') {
      document.title = 'Connect Email to Firm';
    }
    const openerClient = !this.embedded && typeof window !== 'undefined' && window.opener && window.opener.__NU_EMAIL_CLIENT__;
    const globalClient = typeof window !== 'undefined' && window.__NU_EMAIL_CLIENT__;
    this.client = this.sharedClient || openerClient || globalClient || createNuEmailClient();
    this.connectStaff = this.$store.getters.username || '';
    if (!this.noteStatusItems.some((i) => i.text === this.connectStatus)) {
      this.connectStatus = (this.noteStatusItems.find((i) => i.text === 'normal') || this.noteStatusItems[0]).text;
    }
    this.bootstrapStaffUsers();
    this.loadMessage({});
  },
  beforeDestroy() {
    if (this.firmSearchTimer) clearTimeout(this.firmSearchTimer);
  },
  methods: {
    needsEmailApi,

    focusConnectFirmField() {
      this.$nextTick(() => {
        const field = this.$refs.connectFirmField;
        if (field && typeof field.focus === 'function') field.focus();
      });
    },

    requireConnectFirmSelected() {
      const fid = this.resolveConnectFirmId();
      if (Number.isFinite(fid) && fid > 0) return fid;
      this.connectFirmError = true;
      this.snack({ color: 'warning', msg: this.connectFirmRequiredMsg });
      this.focusConnectFirmField();
      return NaN;
    },

    snack(payload) {
      this.$store.dispatch('SET_SNACK', { cmpnt: 'EmailMessage', open: true, ...payload });
    },

    attachmentUrlFor(att) {
      if (!att || !att.attachmentId || !this.mailboxId || !this.messageId) return '';
      const apiBase = process.env.VUE_APP_API_URL || '';
      return gmailAttachmentUrl(this.mailboxId, this.messageId, att.attachmentId, apiBase);
    },

    /** After render — resolve cid: imgs; alt/placeholder fallback when load still fails. */
    onEmailBodyIframeLoad() {
      const iframe = this.$refs.emailBodyIframe;
      if (!iframe) return;
      bindIframeAutoResize(iframe, { maxHeight: EMAIL_IFRAME_MAX_HEIGHT, minHeight: 320 });
      this.fixUnresolvedInlineImages();
    },

    fixUnresolvedInlineImages() {
      const iframe = this.$refs.emailBodyIframe;
      const host = iframe && iframe.contentDocument && iframe.contentDocument.body;
      if (!host || !this.detail) return;

      const attachments = this.detail.attachments || [];
      const cidImgs = host.querySelectorAll('img[src^="cid:"], img[src^="CID:"]');
      const cidCount = cidImgs.length;

      cidImgs.forEach((img, cidIndex) => {
        const cidSrc = img.getAttribute('src') || '';
        const att = resolveInlineImageAttachment(cidSrc, attachments, { cidIndex, cidCount });
        const url = att ? this.attachmentUrlFor(att) : '';
        if (url) img.src = url;
      });

      host.querySelectorAll('img').forEach((img) => {
        const onFail = () => applyFailedEmailImgFallback(img);
        img.addEventListener('error', onFail, { once: true });
        if (img.complete && img.naturalWidth === 0) onFail();
      });
    },

    syncAttachmentStaging(full) {
      const raw = full && Array.isArray(full.attachments) ? full.attachments : [];
      this.attachmentStaging = raw.filter((a) => a && a.filename).map((a) => ({
        filename: a.filename,
        content_type: a.content_type,
        attachmentId: a.attachmentId, // 11 Aug 2026 JCH: selected convert attachments are identified server-side by Gmail id
        file_description: String(a.filename || ''),
      }));
    },

    downloadAttachment(att) {
      if (!att || !att.attachmentId) {
        this.snack({ color: 'error', msg: 'This attachment cannot be downloaded.' });
        return;
      }
      if (!this.client || typeof this.client.downloadAttachment !== 'function') {
        this.snack({ color: 'error', msg: 'Attachment download is not available.' });
        return;
      }
      this.client.downloadAttachment(this.mailboxId, this.messageId, att)
        .catch(() => this.snack({ color: 'error', msg: 'Attachment download failed.' }));
    },

    formatReceived,

    scheduleFirmSearch(q) {
      if (this.firmSearchTimer) clearTimeout(this.firmSearchTimer);
      this.firmSearchTimer = setTimeout(() => this.fetchFirms(q), FIRM_SEARCH_DEBOUNCE_MS);
    },

    /** Blur (e.g. clicking Note status) wipes uncommitted text and would cancel the
     * debounced lookup — flush it now so a just-typed FID still auto-selects. */
    onFirmFieldBlur() {
      if (!this.firmSearchTimer) return;
      clearTimeout(this.firmSearchTimer);
      this.firmSearchTimer = null;
      this.fetchFirms(this.firmSearchInput);
    },

    async fetchFirms(q) {
      const s = (q == null ? '' : String(q)).trim();
      if (!s) { // blur artifact — an emptied box must not drop a made selection
        if (!this.selectedFirm) this.firmItems = [];
        this.firmSearchLoading = false;
        return;
      }
      if (this.selectedFirm && s !== this.selectedFirm.text && s !== String(this.selectedFirm.fid)) {
        this.selectedFirm = null;
      }
      if (this.selectedFirm && s === this.selectedFirm.text) {
        this.firmSearchLoading = false;
        return;
      }
      const isFidSearch = FID_SEARCH_RE.test(s);
      if (!isFidSearch && s.length < 2) {
        this.firmItems = [];
        this.firmSearchLoading = false;
        return;
      }
      this.firmSearchLoading = true;
      try {
        const data = isFidSearch
          ? await firmsAPIService.getFirm(firmSearchPayloadForFid(Number(s)), this.$router)
          : await firmsAPIService.getUnisearchFirm({ search: s, limit: 50 }, this.$router);
        const rows = isFidSearch ? firmRowsFromFidResponse(data) : (Array.isArray(data) ? data : []);
        const items = firmSearchItemsFromRows(rows);
        this.firmItems = items;
        if (isFidSearch && items.length === 1 && Number(items[0].fid) === Number(s)) {
          this.selectedFirm = items[0];
        }
      } catch (e) {
        this.firmItems = [];
      } finally {
        this.firmSearchLoading = false;
      }
    },

    bootstrapStaffUsers() {
      this.staffUsersLoading = true;
      usersAPIService.getActiveUsers(this.$router).then((data) => {
        this.staffUserItems = Array.isArray(data) ? data : [];
        this.ensureConnectStaffValid();
      }).catch(() => {
        this.staffUserItems = [];
      }).finally(() => {
        this.staffUsersLoading = false;
      });
    },

    ensureConnectStaffValid() {
      const names = this.staffUserItems.map((u) => u && u.username).filter(Boolean);
      const un = this.$store.getters.username || '';
      if (names.includes(this.connectStaff)) return;
      if (names.includes(un)) this.connectStaff = un;
      else if (names.length) this.connectStaff = names[0];
      else this.connectStaff = un;
    },

    resolveConnectFirmId() {
      if (this.selectedFirm && this.selectedFirm.fid !== undefined && this.selectedFirm.fid !== null) {
        const n = Number(this.selectedFirm.fid);
        if (Number.isFinite(n) && n > 0) return n;
      }
      return NaN;
    },

    notifyEmailListRefresh(opts = {}) {
      const msg = {
        type: 'email-refresh',
        keepPage: !!opts.keepPage,
      };
      if (opts.highlightId) msg.highlightId = opts.highlightId;
      if (opts.endNotice) msg.endNotice = opts.endNotice;
      if (this.embedded) {
        this.$emit('refresh', msg);
        return;
      }
      try {
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage(msg, window.location.origin);
        }
      } catch (e) { /* noop */ }
    },

    appendSnackNotice(snackPayload, notice) {
      const snack = snackPayload && typeof snackPayload === 'object'
        ? { ...snackPayload }
        : { msg: String(snackPayload || '') };
      if (!notice) return snack;
      snack.msg = `${snack.msg || ''} ${notice}`.trim();
      return snack;
    },

    /**
     * After trash or full connect success: Older/Newer on this page, Message list closes
     * back to the message list, or end panel when none left. Returns true when handled.
     */
    async tryAutoAdvanceAfterAction(snackPayload) {
      const pref = readAutoAdvancePref();
      if (pref === 'off') return false; // Stay
      if (pref === 'list') {
        const msg = (snackPayload && snackPayload.msg) ? String(snackPayload.msg).trim() : '';
        this.notifyEmailListRefresh({ keepPage: true, endNotice: msg || undefined });
        this.backToMailList();
        return true;
      }
      const api = this.autoAdvanceApi();
      if (!api || typeof api.resolveAutoAdvance !== 'function') return false;

      let result;
      try {
        result = api.resolveAutoAdvance(this.messageId);
      } catch (e) {
        return false;
      }
      if (!result || result.skipped) return false;

      if (result.end) {
        // Main card hides on page-end — keep nid for end-panel Open note in firm action.
        if (this.connectedNoteNid) {
          this.rememberLastConnect(this.connectedNoteNid, this.connectedNoteFid);
          this.connectedNoteNid = null;
          this.connectedNoteFid = null;
          this.connectedPartial = false;
          this.connectedResultMsg = '';
          this.connectedTrashed = false;
        }
        const endSnack = this.appendSnackNotice(
          snackPayload,
          result.notice || AUTO_ADVANCE_END_NOTICE,
        );
        this.pageEnd = true;
        this.pageEndHasMoreListPages = !!result.hasMoreListPages;
        this.pageEndActionMsg = (snackPayload && snackPayload.msg) ? String(snackPayload.msg).trim() : '';
        this.snack(endSnack);
        this.notifyEmailListRefresh({ keepPage: true });
        return true;
      }

      if (!result.next || !result.next.message_id) return false;

      this.pageEnd = false;
      this.pageEndHasMoreListPages = false;
      this.lastAdvanceOpenedAs = result.openedAs || null;
      this.lastAdvanceUsedFallback = !!result.usedFallback;
      // Sticky owns "Showing next…" when Open-note strip is up; snack stays outcome-only.
      if (this.lastConnectedNid) {
        this.snack(snackPayload);
      } else {
        this.snack(this.appendSnackNotice(snackPayload, result.notice));
      }
      this.notifyEmailListRefresh({ keepPage: true, highlightId: result.nextId });
      this._advanceTargetId = String(result.next.message_id);
      await this.advanceToNext(result.next);
      return true;
    },

    autoAdvanceApi() {
      if (this.embedded) return this.listApi;
      try {
        if (!window.opener || window.opener.closed) return null;
        return window.opener.__NU_EMAIL_LIST__;
      } catch (e) {
        return null;
      }
    },

    /** Popup ‹ › state from the opener list; recomputed per message load. */
    refreshBrowsePos() {
      if (this.embedded) {
        this.browsePos = null;
        return;
      }
      const api = this.autoAdvanceApi();
      if (!api || typeof api.browsePosition !== 'function') {
        this.browsePos = null;
        return;
      }
      try {
        const pos = api.browsePosition(this.messageId);
        this.browsePos = pos && pos.index >= 0 ? pos : null;
      } catch (e) {
        this.browsePos = null;
      }
    },

    /** Manual ‹ › — just show the neighbor, no connect/trash involved. */
    async browseNeighbor(dir) {
      const api = this.autoAdvanceApi();
      if (!api || typeof api.resolveBrowse !== 'function') return;
      let result = null;
      try {
        result = api.resolveBrowse(this.messageId, dir);
      } catch (e) {
        return;
      }
      if (!result || !result.next) {
        this.refreshBrowsePos(); // list changed under us — resync arrow state
        return;
      }
      await this.advanceToNext(result.next);
    },

    async advanceToNext(next) {
      if (this.embedded) {
        this.$emit('advance', { ...next });
        return;
      }
      await this.$router.replace({ name: 'EmailMessage', query: next });
    },

    rememberLastConnect(nid, fid) {
      if (!nid) return;
      this.lastConnectedNid = nid;
      this.lastConnectedFid = fid != null && fid !== '' ? fid : null;
    },

    clearLastConnectSticky() {
      this.lastConnectedNid = null;
      this.lastConnectedFid = null;
      this.lastAdvanceOpenedAs = null;
      this.lastAdvanceUsedFallback = false;
    },

    viewLastConnectedNote() {
      if (this.lastConnectedNid) {
        this.$_viewFirmNotePopup(this.lastConnectedNid);
      }
    },

    closePopupWindow() {
      if (this.embedded) {
        this.$emit('close');
        return;
      }
      try {
        window.close();
      } catch (e) { /* noop */ }
    },

    backToMailList() {
      if (this.embedded) {
        this.$emit('close');
        return;
      }
      try {
        if (window.opener && !window.opener.closed) {
          window.opener.focus();
        }
      } catch (e) { /* noop */ }
      this.closePopupWindow();
    },

    maybeOpenNoteAfterConnect(nid) {
      if (!nid || !this.openNoteAfterConnect) return;
      this.$_viewFirmNotePopup(nid);
    },

    resetMessageSessionState() {
      this.sessionTrashed = false;
      this.connectedNoteNid = null;
      this.connectedNoteFid = null;
      this.connectedPartial = false;
      this.connectedResultMsg = '';
      this.connectedTrashed = false;
      this.selectedFirm = null;
      this.firmItems = [];
      this.firmSearchInput = '';
      this.connectFirmError = false;
      this.confirmTrashSingle = false;
      this.pageEnd = false;
      this.pageEndActionMsg = '';
      this.pageEndHasMoreListPages = false;
    },

    async loadMessage(opts = {}) {
      const suppressMissingSnack = !!(opts && opts.suppressMissingSnack);
      if (this.missingParams) {
        this.detail = null;
        this.attachmentStaging = [];
        this.connectReLine = '';
        this.resetMessageSessionState();
        this._messageStateKey = null;
        return;
      }
      const stateKey = `${this.mailboxId}|${this.messageId}`;
      if (this._messageStateKey !== stateKey) {
        this.resetMessageSessionState();
        this._messageStateKey = stateKey;
      }
      // 13 Jul 2026 JCH: row click / ‹ › browse isn't an advance — drop the sticky's
      // "Showing next…" line (re-clicking the green row shows the same email).
      const advanceLoad = this._advanceTargetId != null
        && String(this._advanceTargetId) === this.messageId;
      this._advanceTargetId = null;
      if (!advanceLoad) {
        this.lastAdvanceOpenedAs = null;
        this.lastAdvanceUsedFallback = false;
      }
      this.loading = true;
      this.detail = null;
      this.attachmentStaging = [];
      this.connectReLine = '';
      try {
        const full = await this.client.getMessage(this.mailboxId, this.messageId);
        this.detail = full;
        if (full) {
          this.syncAttachmentStaging(full);
          this.connectReLine = full.subject != null ? String(full.subject) : '';
        }
        if (!full && !suppressMissingSnack) {
          this.snack({ color: 'warning', msg: 'Email not found.' });
        }
        this.$nextTick(() => this.fixUnresolvedInlineImages());
      } catch (e) {
        this.snack({ color: 'error', msg: 'Failed to load email.' });
      } finally {
        this.loading = false;
        this.refreshBrowsePos();
      }
    },

    onTrashAfterConnectChange(val) {
      writeTrashAfterConnectPref(!!val);
    },

    onOpenNoteAfterConnectChange(val) {
      writeOpenNoteAfterConnectPref(!!val);
      this.openNoteAfterConnect = readOpenNoteAfterConnectPref();
    },

    onAutoAdvanceModeChange(mode) {
      const next = resolveAutoAdvancePref(mode, this.autoAdvanceGoTo);
      writeAutoAdvancePref(next);
      this.autoAdvance = readAutoAdvancePref();
      this.autoAdvanceMode = autoAdvanceModeFromPref(this.autoAdvance);
      this.autoAdvanceGoTo = autoAdvanceGoToFromPref(this.autoAdvance);
    },

    onAutoAdvanceGoToChange(goTo) {
      const next = resolveAutoAdvancePref(AUTO_ADVANCE_MODE_GO, goTo);
      writeAutoAdvancePref(next);
      this.autoAdvance = readAutoAdvancePref();
      this.autoAdvanceMode = AUTO_ADVANCE_MODE_GO;
      this.autoAdvanceGoTo = autoAdvanceGoToFromPref(this.autoAdvance);
    },

    async maybeTrashAfterConnect(messageIds, rowResult) {
      if (
        this.sessionTrashed
        || !isFullConnectSuccess(rowResult)
        || !this.trashAfterConnect
        || !messageIds.length
      ) {
        return { trashed: 0, failed: 0 };
      }
      return trashMessagesAfterConnect(this.client, this.mailboxId, messageIds);
    },

    async submitConnect() {
      const fid = this.requireConnectFirmSelected();
      if (!Number.isFinite(fid) || fid <= 0) return;
      const username = this.$store.getters.username;
      if (!username) {
        this.snack({ color: 'error', msg: 'Not signed in — cannot connect emails.' });
        return;
      }
      if (!this.connectStaff) {
        this.snack({ color: 'warning', msg: 'Choose Staff / For.' });
        return;
      }
      if (!this.detail || !this.mailboxId || !this.messageId) return;
      if (this.messageTrashed || this.detail.deleted || this.detail.connected) return;

      this.connectBusy = true;
      try {
        const row = await this.client.getMessage(this.mailboxId, this.messageId);
        if (!row) {
          this.snack({ color: 'error', msg: 'Email could not be loaded.' });
          return;
        }
        if (row.deleted || row.connected) {
          await this.loadMessage({});
          return;
        }

        let mailboxLabel = this.mailboxId;
        try {
          const mailboxes = await this.client.listMailboxes();
          const m = Array.isArray(mailboxes) ? mailboxes.find((x) => x && x.id === this.mailboxId) : null;
          if (m && m.name) mailboxLabel = m.name;
        } catch (e) { /* keep id */ }

        const { importedNotes, results } = await runEmailConnectBatch({
          client: this.client,
          router: this.$router,
          username,
          mailboxLabel,
          mailboxId: this.mailboxId,
          messageRows: [{ id: this.messageId }],
          fid,
          status: this.connectStatus,
          staffUsername: this.connectStaff,
          attachmentsOverridesByMessageId: {
            [String(this.messageId)]: this.attachmentStaging.map((a) => ({ ...a })),
          },
          messageDetailsById: { [String(this.messageId)]: row },
          reByMessageId: { [String(this.messageId)]: this.connectReLine },
        });

        const rowResult = Array.isArray(results) && results.length ? results[0] : null;
        if (!rowResult || !rowResult.noteCreated) {
          // 11 Jul 2026 JCH: hard fail → ErrorDialog (For Developers), not raw snack
          if (this.$refs.errorDialog) {
            this.$refs.errorDialog.showError(connectHardFailDialogOptions(rowResult));
          }
          return;
        }
        const created = Array.isArray(importedNotes) ? importedNotes[0] : null;
        const partial = !isFullConnectSuccess(rowResult);
        let snackPayload = {
          color: partial ? 'warning' : undefined,
          msg: connectRowSnackMessage(rowResult, fid),
        };
        const trashOutcome = await this.maybeTrashAfterConnect([this.messageId], rowResult);
        snackPayload = appendTrashAfterConnectSnack(snackPayload, trashOutcome);
        const autoTrashed = trashOutcome.trashed > 0;
        const preserveNid = created && created.nid ? created.nid : null;
        const preserveFid = created && created.nid
          ? (created.fid != null ? created.fid : fid)
          : null;
        const preservePartial = partial;
        // Partial: durable banner gets plain options; snack keeps the short outcome.
        const preserveMsg = partial
          ? CONNECT_PARTIAL_BANNER_HINT
          : connectRowSnackMessage(rowResult, fid);
        const preserveTrashed = autoTrashed || this.sessionTrashed || !!(this.detail && this.detail.deleted);

        if (preserveNid) {
          this.rememberLastConnect(preserveNid, preserveFid);
          this.maybeOpenNoteAfterConnect(preserveNid);
        }

        // Full success: advance when pref on; sticky keeps Open note in firm. Partial: stay + banner.
        if (!partial) {
          const advanced = await this.tryAutoAdvanceAfterAction({
            ...snackPayload,
            msg: connectSnackForAdvance(snackPayload.msg),
          });
          if (advanced) return;
        }

        this.snack(snackPayload);
        this.notifyEmailListRefresh();
        await this.loadMessage({});
        if (preserveNid) {
          this.connectedNoteNid = preserveNid;
          this.connectedNoteFid = preserveFid;
          this.connectedPartial = preservePartial;
          this.connectedResultMsg = preserveMsg;
          this.connectedTrashed = preserveTrashed;
        }
      } finally {
        this.connectBusy = false;
      }
    },

    viewConnectedFirm() {
      if (this.connectedNoteNid) {
        this.$_viewFirmNotePopup(this.connectedNoteNid);
        return;
      }
      if (this.connectedNoteFid) this.$_viewFirmNoNotePopup(this.connectedNoteFid);
    },

    async runTrashCurrentMessage() {
      if (!this.mailboxId || !this.messageId) {
        this.confirmTrashSingle = false;
        return;
      }
      this.busy = true;
      const preserveNid = this.connectedNoteNid;
      const preserveFid = this.connectedNoteFid;
      const preservePartial = this.connectedPartial;
      const preserveMsg = this.connectedResultMsg;
      try {
        await this.client.softDeleteMessages(this.mailboxId, [this.messageId]);
        this.sessionTrashed = true;
        this.confirmTrashSingle = false;
        const advanced = await this.tryAutoAdvanceAfterAction({
          msg: 'Previous email moved to trash.',
        });
        if (advanced) return;
        this.snack({ msg: 'Email moved to trash.' });
        this.notifyEmailListRefresh();
        if (preserveNid) {
          await this.loadMessage({ suppressMissingSnack: true });
          this.connectedNoteNid = preserveNid;
          this.connectedNoteFid = preserveFid;
          this.connectedPartial = preservePartial;
          this.connectedResultMsg = preserveMsg;
          this.connectedTrashed = true;
        } else {
          this.detail = null;
          this.attachmentStaging = [];
          this.connectReLine = '';
        }
      } catch (e) {
        this.snack({ color: 'error', msg: 'Could not move to Trash.' });
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
/* Box hugs content (like page-end card) so the dismiss X stays next to the text. */
.email-last-connect-strip {
  border: 1px solid rgba(0, 0, 0, 0.08);
  max-width: 560px;
}

.email-last-connect-copy {
  min-width: 0;
}

.email-last-connect-note-btn {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 500;
  height: auto !important; /* label wraps in narrow panes — fixed v-btn height clips it */
  max-width: 100%;
  min-height: 28px;
  padding-bottom: 2px;
  padding-top: 2px;
}

.email-last-connect-note-btn >>> .v-btn__content {
  flex-shrink: 1;
  text-align: left;
  white-space: normal;
}

.email-page-end-card {
  max-width: 560px;
}

.email-meta {
  background: #fff;
  line-height: 1.35;
}
.email-meta-line:not(:last-child) {
  margin-bottom: 2px;
}

.email-attachments-inline-note {
  font-size: 0.8125rem; /* hair under body-2 — first sentence fits one line at default width */
  line-height: 1.35;
}

.email-body-iframe {
  border: 1px solid rgba(0, 0, 0, 0.12);
  display: block;
  width: 100%;
}

.email-connect-panel {
  background: #eeeeee;
  border: 2px solid #616161;
}

.email-firm-autocomplete {
  min-width: 0;
  width: 100%;
}

.email-connect-panel-title {
  letter-spacing: 0.01em;
  line-height: 1.25;
}

.email-connect-band--form {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.email-connect-re {
  max-width: 100%;
}

.email-connect-fields {
  align-items: flex-end;
  column-gap: 12px;
  display: flex;
  flex-wrap: wrap;
  row-gap: 8px;
  width: 100%;
}

.email-connect-fields .email-connect-staff,
.email-connect-fields .email-connect-status {
  flex: 1 1 0;
  min-width: 0;
}

.email-connect-band--commit {
  align-items: center;
  border-top: 1px solid #757575;
  column-gap: 8px;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  margin-top: 10px;
  padding-top: 10px;
  row-gap: 4px;
}

.email-connect-split {
  border-radius: 4px;
  overflow: hidden;
}

.email-connect-btn--split-main {
  border-bottom-right-radius: 0 !important;
  border-top-right-radius: 0 !important;
  min-width: 0;
}

.email-connect-btn--split-caret {
  border-bottom-left-radius: 0 !important;
  border-left: 1px solid rgba(255, 255, 255, 0.35) !important;
  border-top-left-radius: 0 !important;
  min-width: 36px !important;
  padding-left: 4px !important;
  padding-right: 4px !important;
}

.email-connect-trash-btn:not(.v-btn--disabled) {
  color: rgba(0, 0, 0, 0.67) !important;
}

.email-connect-menu-subhead {
  font-size: 0.7rem;
  height: auto;
  line-height: 1.2;
  padding-bottom: 4px;
  padding-top: 4px;
}

.email-connect-menu-prefs >>> .v-input--checkbox {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.email-connect-menu-prefs >>> .v-input--selection-controls {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.email-connect-menu-prefs >>> .v-input--selection-controls__input {
  height: 22px;
  margin-right: 2px;
  width: 22px;
}

.email-connect-menu-prefs >>> .v-input--selection-controls .v-icon {
  font-size: 18px;
}

.email-connect-menu-prefs >>> .v-input--selection-controls .v-label {
  font-size: 0.75rem;
  line-height: 1.25;
  margin-bottom: 0;
  white-space: normal;
}

.email-trash-after-connect,
.email-open-note-after-connect {
  flex: 0 0 auto;
  min-height: 28px !important;
  min-width: 0;
}

/* 11 Jul 2026 JCH: Stay / Go to + destination when leaving.
   13 Jul 2026 JCH: size container — caption font shrinks with pane width (drag floor
   360px → ~276px here) instead of ellipsizing. Basis/min match the selects row. */
.email-auto-advance-pair {
  container-type: inline-size;
  flex: 1 1 274px;
  min-width: 274px;
}

.email-auto-advance-caption {
  font-size: 0.75rem;
  letter-spacing: 0.015em;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@container (max-width: 332px) {
  .email-auto-advance-caption {
    font-size: clamp(0.625rem, 3.75cqw, 0.75rem);
  }
}

.email-auto-advance-selects {
  column-gap: 10px;
  row-gap: 4px;
}

.email-auto-advance-mode {
  flex: 0 0 104px;
}

.email-auto-advance-goto {
  /* 13 Jul 2026 JCH: fits the widest option, "Message list", without truncating */
  flex: 0 0 160px;
}

.email-connect-outcome {
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.email-connect-outcome--partial {
  border-color: rgba(251, 140, 0, 0.45);
}

.email-connect-success-row {
  column-gap: 8px;
  row-gap: 8px;
}

.email-connect-success-actions {
  column-gap: 8px;
  max-width: 100%;
  min-width: 0;
  row-gap: 8px; /* only felt when the trash button wraps under the note button */
}

@media (max-width: 959px) {
  .email-connect-btn.v-btn.v-size--small {
    letter-spacing: 0.02em;
    min-width: 72px !important;
    padding: 0 10px !important;
  }
}

@media (min-width: 940px) {
  .email-connect-fields .email-connect-staff >>> .v-select__selection,
  .email-connect-fields .email-connect-status >>> .v-select__selection {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .email-connect-btn--main.v-btn.v-size--x-large {
    font-weight: 500;
    letter-spacing: 0.02em;
    min-width: 100px !important;
    padding: 0 14px !important;
  }
}


.email-attachment-desc {
  max-width: min(560px, 94vw);
}

/* 6 May 2026 JCH: underline dense inputs keep Vuetify's default active transform (-18px), not filled's (-10px).
   Shift raised labels down 4px: -18px + 4px. */
.email-connect-panel >>> .v-input--dense .v-label.v-label--active {
  transform: translateY(calc(-18px + 4px)) scale(0.75) !important;
}
</style>
